import { useCallback, useEffect } from "react";

import { type SectionVersionModelUpdateType } from "../shared/types/pubsub";

type MessageCallback<T> = (message: T) => void;

export enum BROADCAST_TOPICS {
  PUBSUB_MODEL_UPDATE = "pubsub_model_update",
  AI_PAGE_ENHANCED = "ai_page_enhanced",
  AI_RUBRIC_GENERATED = "ai_rubric_generated",
  AI_OUTCOMES_TAGGED = "ai_outcomes_tagged",
}

type Topic = (typeof BROADCAST_TOPICS)[keyof typeof BROADCAST_TOPICS];

type AIPageActionMessage = {
  pageId: string;
};

type TopicMessageMap = {
  [BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE]: SectionVersionModelUpdateType;
  [BROADCAST_TOPICS.AI_PAGE_ENHANCED]: AIPageActionMessage;
  [BROADCAST_TOPICS.AI_RUBRIC_GENERATED]: AIPageActionMessage;
  [BROADCAST_TOPICS.AI_OUTCOMES_TAGGED]: AIPageActionMessage;
};

// Map of topics to sets of callbacks
const broadcastCallbacks = new Map<Topic, Set<MessageCallback<TopicMessageMap[Topic]>>>();

/**
 * Send a message to all listeners registered for a specific topic.
 * @param topic The topic to broadcast to
 * @param message The message to broadcast
 */
export function sendBroadcast<T extends Topic>(topic: T, message: TopicMessageMap[T]): void {
  const callbacks = broadcastCallbacks.get(topic);
  if (callbacks) {
    callbacks.forEach((cb) => cb(message));
  }
}

/**
 * Register a callback to be called whenever we get a broadcast message for a specific topic.
 * The callback is responsible for determining whether the message is relevant.
 * @param topic The topic to listen to
 * @param callback The callback to be called when a message is received
 * @returns A function to unsubscribe from the topic
 */
export const addBroadcastListener = <T extends Topic>(
  topic: T,
  callback: MessageCallback<TopicMessageMap[T]>,
) => {
  if (!broadcastCallbacks.has(topic)) {
    broadcastCallbacks.set(topic, new Set());
  }
  const callbacks = broadcastCallbacks.get(topic)!;
  callbacks.add(callback);

  return () => {
    callbacks.delete(callback);
  };
};

export const useBroadcastListener = <T extends Topic>(
  topic: T,
  callback: MessageCallback<TopicMessageMap[T]>,
  callbackDeps: unknown[],
) => {
  const cachedCallback = useCallback((message: TopicMessageMap[T]) => {
    return callback(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, callbackDeps);
  useEffect(() => {
    return addBroadcastListener(topic, cachedCallback);
  }, [topic, cachedCallback]);
};
